import { defineStore } from 'pinia';

// grace period (ms) to keep the overlay up after the last request settles, so a follow-up
// request that fires right after (e.g. a query chained off another) doesn't cause the
// overlay to flicker off and back on.
const HIDE_DELAY_MS = 300;

/**
 * Tracks the number of in-flight API requests so a global blocking overlay can be shown
 * while any AJAX call is pending. Incremented/decremented by the global fetch interceptor
 * (see plugins/6.global-loading.client.ts).
 *
 * `isLoading` turns on immediately when a request starts, but turning off is delayed by
 * HIDE_DELAY_MS once the count reaches zero — and cancelled if a new request arrives in the
 * meantime — to avoid blinking between back-to-back requests.
 */
export const useGlobalLoadingStore = defineStore('global-loading', () => {
  const pendingCount = ref(0);
  const isLoading = ref(false);

  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const clearHideTimer = () => {
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const start = () => {
    pendingCount.value++;
    // a new request is in flight: keep showing and cancel any pending hide
    clearHideTimer();
    isLoading.value = true;
  };

  const done = () => {
    // never let the counter go negative if something double-settles
    pendingCount.value = Math.max(0, pendingCount.value - 1);
    if (pendingCount.value === 0) {
      // wait a beat before hiding; a follow-up request will cancel this via start()
      clearHideTimer();
      hideTimer = setTimeout(() => {
        hideTimer = null;
        if (pendingCount.value === 0) isLoading.value = false;
      }, HIDE_DELAY_MS);
    }
  };

  // safety valve (e.g. on route change) in case a request never settles
  const reset = () => {
    clearHideTimer();
    pendingCount.value = 0;
    isLoading.value = false;
  };

  return { pendingCount, isLoading, start, done, reset };
});
